<template>
  <v-col cols="12">
    <div class="mb-4 questionText d-flex align-start">
      <v-chip
        v-if="validated && invalid"
        color="red darken-2 white--text"
        class="text-capitalize mr-3 flex-shrink-0"
        >Required
      </v-chip>
      <template v-else>
        <div
          class="mr-3"
          v-if="question.questionNumber && question.questionNumber.length > 0"
        >
          {{ question.questionNumber }}
        </div>
      </template>
      <survey-question-text :text="question.text"></survey-question-text>
    </div>
    <Validation-Observer ref="observer" tag="div">
      <Validation-Provider
        v-for="n in slotCount"
        :key="`slot-${n}`"
        :rules="{
          required
        }"
        v-slot="{ errors }"
      >
        <v-select
          placeholder="--"
          :value="fieldValue[n - 1]"
          :items="selectItems"
          outlined
          :error-messages="errors"
          @input="e => updateInnerValue(n - 1, e)"
        >
          <template v-slot:prepend>
            <v-chip
              :color="
                `${
                  validated && invalid
                    ? 'red darken-2 white--text'
                    : 'secondary'
                }`
              "
              class="rank-ind text-capitalize rounded-circle mr-3 flex-shrink-0 mt-n2"
            >
              {{ n }}
            </v-chip>
          </template>
        </v-select>
      </Validation-Provider>
    </Validation-Observer>
  </v-col>
</template>

<script>
import Vue from "vue";
import {
  getQuestionSetting,
  questionSettings,
  showQuestion,
  requireQuestion
} from "../constants/Survey";

import SurveyQuestionText from "./Survey.Question.Text.vue";

export default {
  name: "RankedChoiceField",
  components: {
    SurveyQuestionText
  },
  data: () => ({
    innerValue: [],
    validated: false
  }),
  props: {
    value: {
      type: Array,
      default: () => []
    },
    question: {
      type: Object
    }
  },
  computed: {
    fieldIndex() {
      return this.innerValue.findIndex(x => x.key === this.question.key);
    },
    fieldValue() {
      let target = this.innerValue[this.fieldIndex];
      return target && target.value ? target.value : [];
    },
    slotCount() {
      let result = getQuestionSetting(
        this.question.settings,
        questionSettings.slotCount.key
      ).value;

      if (!isNaN(result) && result <= this.question.options.length)
        return +result;
      else return this.question.options.length;
    },
    required() {
      return (
        getQuestionSetting(
          this.question.settings,
          questionSettings.required.key
        ).value == "true" || requireQuestion(this.question, this.innerValue)
      );
    },
    hasValue() {
      return (
        this.fieldValue.some(x => x !== null) && this.fieldValue.length > 0
      );
    },
    completed() {
      return (
        this.fieldValue.length === this.slotCount &&
        !this.fieldValue.some(x => !x)
      );
    },
    valid() {
      return this.errors.length === 0;
    },
    invalid() {
      return !this.valid;
    },
    errors() {
      let results = [];

      if (this.required && !this.completed)
        results.push({ key: this.question.key, value: "Required" });

      return results;
    },
    selectItems() {
      return [
        {
          value: null,
          text: "--"
        }
      ].concat(this.question.options.slice());
    },
    visible() {
      return showQuestion(this.question, this.innerValue);
    }
  },
  watch: {
    value(val, prev) {
      let test = JSON.stringify(val);
      if (test == JSON.stringify(prev)) return;
      if (test != JSON.stringify(this.innerValue)) {
        this.innerValue = JSON.parse(test);
      }
    },
    innerValue(value, prev) {
      let test = JSON.stringify(value);
      if (test == JSON.stringify(prev)) return;

      this.$emit("input", this.innerValue);
    }
  },
  methods: {
    setKeyPair(value) {
      let keyPair = { key: this.question.key, value };

      let index = this.innerValue.findIndex(x => x.key === this.question.key);
      if (index > -1) Vue.set(this.innerValue, index, keyPair);
      else this.innerValue.push(keyPair);
    },
    getOptionByVal(val) {
      return this.question.options.find(x => x.value === val);
    },
    updateInnerValue(index, value) {
      let val = JSON.parse(JSON.stringify(this.fieldValue));
      let keyPair = JSON.parse(
        JSON.stringify({ key: this.question.key, value: val })
      );

      keyPair.value.forEach((x, i) => {
        if (x === value && i !== index) keyPair.value[i] = null;
      });

      keyPair.value[index] = value;

      if (index > -1) Vue.set(this.innerValue, this.fieldIndex, keyPair);
      else this.innerValue.push(keyPair);
    },

    validate() {
      this.validated = true;
      this.$refs.observer.validate();
      return this.valid;
    },
    reset() {
      this.validated = false;
      this.$refs.observer.reset();
    }
  },
  mounted() {
    if (this.value) this.innerValue = JSON.parse(JSON.stringify(this.value));
  },
  destroyed() {
    // Clear answer if hidden.
    if (!showQuestion(this.question, this.innerValue)) this.setKeyPair(null);
  }
};
</script>

<style lang="scss" scoped>
.slot {
  &.empty {
    border: 2px dashed rgba(0, 0, 0, 0.15) !important;
    color: rgba(0, 0, 0, 0.4);
  }
}

.rank-ind {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px !important;
  height: 36px !important;
  padding: 0 !important;
  font-weight: bold;
}
</style>
